import { forOwn, isObject, mapValues } from 'lodash-es';

/**
 * Settings for the object converter.
 */
export interface ObjectConverterConfig {
    /**
     * If set, only not null properties are copied.
     */
    skipNullValues: boolean;
}

/**
 * Converter function applied while copying property.
 * @param srcVal Property value from source object.
 * @param defaultValue A default value to be used of value is empty.
 * @returns Value to be stored in the target object.
 */
export type ValueConverter<TSource, TTarget> = (srcVal: TSource, defaultValue?: TTarget) => TTarget;

/**
 *  Converts an object to another using defined mappings and conversions.
 */
export type ObjectConverter<TSource, TTarget> = (source: TSource, configOverride?: ObjectConverterConfig) => TTarget;

export type FieldMappings = _.Dictionary<string[]>;

/**
 * Map to define conversion from one object to another.
 */
export interface ConversionMap<TSource, TTarget> {
    /**
     * Get all registered field mappings (source property -> target properties). Map only contains field names without converters.
     */
    fieldMappings: FieldMappings;

    /**
     * Starts a new mapping.
     * Specifies property in source object from which values are copied to during conversion.
     * @param propName Name of source property.
     */
    from<TPropName extends keyof TSource>(propName: TPropName): FromPropertyConversionMap<TSource, TPropName, TTarget>;

    /**
     * Build the actual converter function for all defined mappings.
     * @returns Converter function.
     */
    build(): ObjectConverter<TSource, TTarget>;

    /**
     * Build the actual converter function with all defined mappings.
     * This converter can be used to convert arrays directly.
     * @returns Converter function for arrays.
     */
    buildMulti(): ObjectConverter<TSource[], TTarget[]>;

    /**
     * Build the actual converter function for all defined reverse mappings.
     * @returns Converter function.
     */
    buildReverse(): ObjectConverter<TTarget, TSource>;

    /**
     * Build the actual converter function for all defined reverse mappings.
     * This converter can be used to convert arrays directly.
     * @returns Converter function.
     */
    buildMultiReverse(): ObjectConverter<TTarget[], TSource[]>;

    /**
     * Extends an existing map.
     * @param parentMap The map to inherit from
     */
    extends<TS extends Partial<TSource>, TT extends Partial<TTarget>>(
        parentMap: ConversionMap<TS, TT>
    ): ConversionMap<TSource, TTarget>;
}

export interface FromPropertyConversionMap<TSource, TSourcePropName extends keyof TSource, TTarget> {
    /**
     * Specifies property in target object to which values are copied to during conversion.
     * @param propName Name of target property.
     * @param converter Optional converter applied before copying the value.
     * @param defaultValue Optional default value used if source property is empty.
     */
    to<TPropName extends keyof TTarget>(
        propName: TPropName,
        converter?: ValueConverter<TSource[TSourcePropName], TTarget[TPropName]>,
        defaultValue?: TTarget[TPropName]
    ): ToPropertyConversionMap<TSource, TSourcePropName, TTarget, TPropName>;
}

export interface ToPropertyConversionMap<
    TSource,
    TSourcePropName extends keyof TSource,
    TTarget,
    TTargetPropName extends keyof TTarget
> extends ConversionMap<TSource, TTarget> {
    /**
     * Defines the reverse mapping (dest -> source).
     * @param converter Optional converter applied before copying the value.
     * @param defaultValue Optional default value used if source property is empty.
     */
    reverse(
        converter?: ValueConverter<TTarget[TTargetPropName], TSource[TSourcePropName]>,
        defaultValue?: TSource[TSourcePropName]
    ): ConversionMap<TSource, TTarget>;
}

interface Conversion<TSource, TSourcePropName extends keyof TSource, TTarget, TTargetPropName extends keyof TTarget> {
    source: TSourcePropName;
    target: TTargetPropName;
    converter: ValueConverter<TSource[TSourcePropName], TTarget[TTargetPropName]>;
    defaultValue?: TTarget[TTargetPropName];
}

type InternalConversionMap<TSource, TTarget> = {
    [TSourcePropName in keyof TSource]?: Conversion<TSource, keyof TSource, TTarget, keyof TTarget>[];
} & _.Dictionary<Conversion<TSource, keyof TSource, TTarget, keyof TTarget>[]>;

const defaultConfig: ObjectConverterConfig = {
    skipNullValues: true
};

class ToPropertyConversionMapImpl<
    TSource,
    TSourcePropName extends keyof TSource,
    TTarget,
    TTargetPropName extends keyof TTarget
>
    implements
        ToPropertyConversionMap<TSource, TSourcePropName, TTarget, TTargetPropName>,
        ConversionMap<TSource, TTarget>
{
    constructor(
        private readonly map: ConversionMapImpl<TSource, TTarget>,
        private readonly sourcePropName: TSourcePropName,
        private readonly targetPropName: TTargetPropName
    ) {}

    get fieldMappings() {
        return this.map.fieldMappings;
    }

    from<TPropName extends keyof TSource>(propName: TPropName) {
        return this.map.from(propName);
    }

    build(): ObjectConverter<TSource, TTarget> {
        return this.map.build();
    }

    buildMulti() {
        return this.map.buildMulti();
    }

    buildReverse(): ObjectConverter<TTarget, TSource> {
        return this.map.buildReverse();
    }

    buildMultiReverse() {
        return this.map.buildMultiReverse();
    }

    extends<TS extends Partial<TSource>, TT extends Partial<TTarget>>(parentMap: ConversionMap<TS, TT>) {
        return this.map.extends(parentMap);
    }

    reverse(
        converter?: ValueConverter<TTarget[TTargetPropName], TSource[TSourcePropName]>,
        defaultValue?: TSource[TSourcePropName]
    ) {
        this.map.addReverse({
            source: this.targetPropName,
            target: this.sourcePropName,
            converter,
            defaultValue
        });
        return this.map;
    }
}

class FromPropertyConversionMapImpl<
    TSource,
    TSourcePropName extends keyof TSource,
    TTarget
> implements FromPropertyConversionMap<TSource, TSourcePropName, TTarget> {
    constructor(
        private readonly map: ConversionMapImpl<TSource, TTarget>,
        private readonly sourcePropName: TSourcePropName
    ) {}

    to<TTargetPropName extends keyof TTarget>(
        propName: TTargetPropName,
        converter?: ValueConverter<TSource[TSourcePropName], TTarget[TTargetPropName]>,
        defaultValue?: TTarget[TTargetPropName]
    ): ToPropertyConversionMap<TSource, TSourcePropName, TTarget, TTargetPropName> {
        this.map.add({
            source: this.sourcePropName,
            target: propName,
            converter,
            defaultValue
        });
        return new ToPropertyConversionMapImpl(this.map, this.sourcePropName, propName);
    }
}

function createObjectConverter<TSource, TTarget>(
    map: InternalConversionMap<TSource, TTarget>,
    globalConfig: ObjectConverterConfig
): ObjectConverter<TSource, TTarget> {
    return (source: TSource, config?: ObjectConverterConfig): TTarget => {
        if (!isObject(source)) {
            return null;
        }

        config = config || globalConfig || defaultConfig;

        const target = {} as TTarget;

        const mapped = {};

        forOwn(source, (v, k: string) => {
            // keyof TSource
            const converters = map[k];
            if (converters && (!config.skipNullValues || v != null)) {
                converters.forEach(conv => {
                    try {
                        // Converter logic will be executed below
                    } catch (e) {
                        console.error('Error in converter', conv);
                    }
                    target[conv.target] = conv.converter
                        ? conv.converter(v, conv.defaultValue)
                        : v != null
                          ? (v as any)
                          : conv.defaultValue;
                });
            }

            if (converters) {
                mapped[k] = true;
            }
        });

        for (const key of Object.keys(map)) {
            if (!mapped[key]) {
                const converters = map[key];
                if (converters && (!config.skipNullValues || source != null)) {
                    converters
                        .filter(e => e.source === null)
                        .forEach(conv => {
                            target[conv.target] = conv.converter
                                ? conv.converter(source as any, conv.defaultValue)
                                : conv.defaultValue;
                        });
                }
            }
        }

        return target;
    };
}

function createMultiObjectConverter<TSource, TTarget>(singleConv: ObjectConverter<TSource, TTarget>) {
    return (source: TSource[], config?: ObjectConverterConfig): TTarget[] =>
        source && source instanceof Array ? source.map(i => singleConv(i, config)) : undefined;
}

class ConversionMapImpl<TSource, TTarget> implements ConversionMap<TSource, TTarget> {
    private readonly map: InternalConversionMap<TSource, TTarget> = {};
    private readonly reverseMap: InternalConversionMap<TTarget, TSource> = {};

    constructor(private readonly config: ObjectConverterConfig = defaultConfig) {}

    get fieldMappings(): FieldMappings {
        return mapValues(this.map as any, convs => convs.map(c => c.target as string));
    }

    from<TPropName extends keyof TSource>(propName: TPropName): FromPropertyConversionMap<TSource, TPropName, TTarget> {
        return new FromPropertyConversionMapImpl<TSource, TPropName, TTarget>(this, propName);
    }

    build(): ObjectConverter<TSource, TTarget> {
        return createObjectConverter<TSource, TTarget>(this.map, this.config);
    }

    buildMulti(): ObjectConverter<TSource[], TTarget[]> {
        return createMultiObjectConverter(this.build());
    }

    buildReverse(): ObjectConverter<TTarget, TSource> {
        return createObjectConverter<TTarget, TSource>(this.reverseMap, this.config);
    }

    buildMultiReverse(): ObjectConverter<TTarget[], TSource[]> {
        return createMultiObjectConverter(this.buildReverse());
    }

    extends<TS extends Partial<TSource>, TT extends Partial<TTarget>>(parentMap: ConversionMap<TS, TT>) {
        if (!('map' in parentMap)) {
            throw new Error('Parent map is not valid.');
        }

        if (((parentMap as unknown as ConversionMapImpl<TS, TT>).map as any) instanceof ConversionMapImpl) {
            parentMap = (parentMap as unknown as ConversionMapImpl<TS, TT>).map as any;
        }

        const { map, reverseMap } = parentMap as ConversionMapImpl<TS, TT>;
        forOwn(map as any, convs => convs.map(conv => this.add(conv as any)));
        forOwn(reverseMap as any, convs => convs.map(conv => this.addReverse(conv as any)));

        return this;
    }

    add<TSourcePropName extends keyof TSource, TTargetPropName extends keyof TTarget>(
        conv: Conversion<TSource, TSourcePropName, TTarget, TTargetPropName>
    ) {
        if (!this.map[conv.source]) {
            this.map[conv.source] = [] as any;
        }

        this.map[conv.source].push(conv);
    }

    addReverse<TTargetPropName extends keyof TTarget, TSourcePropName extends keyof TSource>(
        conv: Conversion<TTarget, TTargetPropName, TSource, TSourcePropName>
    ) {
        if (!this.reverseMap[conv.source]) {
            this.reverseMap[conv.source] = [] as any;
        }
        this.reverseMap[conv.source].push(conv);
    }
}

export function createConversionMap<TSource, TTarget>(config?: ObjectConverterConfig): ConversionMap<TSource, TTarget> {
    return new ConversionMapImpl(config);
}

export default createConversionMap;
